import { type } from 'arktype';

/**
 * Prompt metadata for additional configuration
 */
export const promptMetadataSchema = type({
  /** Tags for categorization */
  'tags?': type('string[]').configure({
    description: 'Optional list of free-form tags used to categorize or filter prompts (nullable when omitted).',
    examples: [
      ['scheduling', 'optimisation'],
      ['chat', 'navigation'],
    ],
  }),
  /** Prompt author */
  'author?': type('string').configure({
    description: 'Display name or identifier of the user who authored the prompt (nullable when omitted).',
    examples: ['admin@zeitag.ch', 'Claudia Steiner'],
  }),
  /** Usage notes */
  'notes?': type('string').configure({
    description: 'Free-form notes explaining how and when to use the prompt (nullable when omitted).',
    examples: ['Used as the primary system prompt for nightly scenario generation jobs.'],
  }),
}).configure({
  description: 'Optional metadata block attached to a prompt for categorisation, authorship, and operational notes.',
});

export type PromptMetadata = typeof promptMetadataSchema.t;

/**
 * Prompt entity as stored in database
 */
export const promptSchema = type({
  id: type('string').configure({
    description: 'Stable unique identifier of the prompt record.',
    examples: ['prompt_01HXYZ123ABC'],
  }),
  name: type('string').configure({
    description: 'Well-known prompt name used to look the prompt up in code (see PROMPT_NAMES).',
    examples: ['scenario-generation-system', 'chat-orchestrator'],
  }),
  'description?': type('string').configure({
    description: 'Human-readable explanation of what the prompt is for (nullable when omitted).',
    examples: ['System prompt that guides the scenario generator toward cost-efficient rosters.'],
  }),
  template: type('string').configure({
    description: 'The raw prompt template text, including any placeholder tokens resolved at runtime.',
    examples: ['Plan next week for the east team. Context: {{context}}'],
  }),
  provider: type('string').configure({
    description: 'Identifier of the AI provider that should execute this prompt.',
    examples: ['openai', 'anthropic', 'azure-openai'],
  }),
  'modelVersion?': type('string').configure({
    description: 'Specific model version pinned for this prompt (nullable to use the provider default).',
    examples: ['gpt-4o-2024-08-06', 'claude-sonnet-4.5'],
  }),
  'temperature?': type('number').configure({
    description: 'Sampling temperature (0-2) applied to model calls (nullable to use the provider default).',
    examples: [0.2, 1],
  }),
  'maxTokens?': type('number').configure({
    description: 'Maximum number of tokens the model may produce (nullable to use the provider default).',
    examples: [512, 4096],
  }),
  'metadata?': promptMetadataSchema,
  version: type('number').configure({
    description: 'Monotonically increasing version number incremented on each edit.',
    examples: [1, 7],
  }),
  isActive: type('boolean').configure({
    description: 'Whether the prompt is currently active and eligible for selection at runtime.',
    examples: [true, false],
  }),
  createdAt: type('Date').configure({
    description: 'Timestamp of when the prompt record was created.',
    examples: ['2026-01-05T08:00:00.000Z'],
  }),
  updatedAt: type('Date').configure({
    description: 'Timestamp of the last update to the prompt record.',
    examples: ['2026-04-22T14:30:00.000Z'],
  }),
}).configure({
  description: 'Prompt entity as persisted in the database with versioning and activation metadata.',
});

export type Prompt = typeof promptSchema.t;

/**
 * Create prompt request
 */
export const createPromptSchema = type({
  name: type('string').configure({
    description: 'Well-known prompt name used to look the prompt up in code.',
    examples: ['scenario-generation-system', 'chat-orchestrator'],
  }),
  'description?': type('string').configure({
    description: 'Human-readable explanation of what the prompt is for (nullable when omitted).',
    examples: ['Primary chat system prompt for the employee assistant.'],
  }),
  template: type('string').configure({
    description: 'Raw prompt template text, including any placeholder tokens resolved at runtime.',
    examples: ['Plan next week for the east team. Context: {{context}}'],
  }),
  provider: type('string').configure({
    description: 'Identifier of the AI provider that should execute this prompt.',
    examples: ['openai', 'anthropic'],
  }),
  'modelVersion?': type('string').configure({
    description: 'Specific model version pinned for this prompt (nullable to use the provider default).',
    examples: ['gpt-4o-2024-08-06'],
  }),
  'temperature?': type('number >= 0 & number <= 2').configure({
    description: 'Sampling temperature clamped to the range 0-2 (nullable to use the provider default).',
    examples: [0.2, 1],
  }),
  'maxTokens?': type('number > 0').configure({
    description: 'Maximum number of tokens the model may produce, must be strictly positive (nullable to use the provider default).',
    examples: [512, 4096],
  }),
  'metadata?': promptMetadataSchema,
}).configure({
  description: 'Request body for creating a new prompt.',
});

export type CreatePrompt = typeof createPromptSchema.t;

/**
 * Update prompt request
 */
export const updatePromptSchema = type({
  'name?': type('string').configure({
    description: 'New well-known prompt name (nullable when not being changed).',
    examples: ['chat-orchestrator'],
  }),
  'description?': type('string').configure({
    description: 'New human-readable description for the prompt (nullable when not being changed).',
    examples: ['Updated scenario generation prompt with fairness emphasis.'],
  }),
  'template?': type('string').configure({
    description: 'New raw prompt template text (nullable when not being changed).',
    examples: ['Plan next week for the east team. Context: {{context}}'],
  }),
  'provider?': type('string').configure({
    description: 'New AI provider identifier (nullable when not being changed).',
    examples: ['openai', 'anthropic'],
  }),
  'modelVersion?': type('string').configure({
    description: 'New pinned model version (nullable when not being changed).',
    examples: ['gpt-4o-2024-08-06'],
  }),
  'temperature?': type('number >= 0 & number <= 2').configure({
    description: 'New sampling temperature in the range 0-2 (nullable when not being changed).',
    examples: [0.2, 1],
  }),
  'maxTokens?': type('number > 0').configure({
    description: 'New maximum token budget (nullable when not being changed).',
    examples: [512, 4096],
  }),
  'metadata?': promptMetadataSchema,
  'isActive?': type('boolean').configure({
    description: 'Toggle the prompt active state (nullable when not being changed).',
    examples: [true, false],
  }),
}).configure({
  description: 'Request body for partially updating an existing prompt.',
});

export type UpdatePrompt = typeof updatePromptSchema.t;

/**
 * Well-known prompt names used in the system
 */
export const PROMPT_NAMES = {
  /** System prompt for scenario generation */
  SCENARIO_SYSTEM: 'scenario-generation-system',
  /** User prompt template for scenario generation */
  SCENARIO_USER: 'scenario-generation-user',
  /** System prompt for auto-fill */
  AUTOFILL_SYSTEM: 'autofill-system',
  /** User prompt template for auto-fill */
  AUTOFILL_USER: 'autofill-user',
  /** System prompt for chat (grounded navigation + fact responses) */
  CHAT_SYSTEM: 'chat-system',
  /** Orchestrator prompt for intent classification and tool selection */
  CHAT_ORCHESTRATOR: 'chat-orchestrator',
  /** Deterministic navigation fallback prompt (no AI call required) */
  CHAT_NAVIGATION_FALLBACK: 'chat-navigation-fallback',
} as const;

export type PromptName = (typeof PROMPT_NAMES)[keyof typeof PROMPT_NAMES];
